import { useCallback } from 'react';

import { useAtom, useAtomValue } from 'jotai';

import { useUserSettingsWithDefaults, useToggle } from '@hooks';
import { useTranslate } from '@i18n';
import { useUpdateUserSettings } from '@queries';
import {
    activeSwingIdAtom,
    activeUserIdAtom,
    hasFullAnalysisAtom,
    showVisualizationsAtom,
    swingThreadToggleAtom,
} from '@store';
import { ToggleButton, PlusIcon, colors, ActionButton, Heading3, Only, Box, ToggleButtonV2, Text, u } from '@ui';

import { ComparisonSwingModal } from '../modals/ComparisonSwingModal/ComparisonSwingModal';
import { VisualizationsModal } from '../modals/VisualizationsModal';

import * as css from './ActivityTopBar.css';

type ActivityTopBarProps = {
    isEditingLayout?: boolean;
    isEditingFloor?: boolean;
    onCreateNote: () => void;
    onEditLayout: () => void;
    onEditFloor: () => void;
};

export const ActivityTopBar = ({
    isEditingLayout,
    isEditingFloor,
    onCreateNote,
    onEditLayout,
    onEditFloor,
}: ActivityTopBarProps) => {
    const [isVisible, toggleVisibility, setIsVisible] = useToggle(false);
    const [showThreads, setShowThreads] = useAtom(swingThreadToggleAtom);

    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const activeUserId = useAtomValue(activeUserIdAtom);
    const { data: userSettings } = useUserSettingsWithDefaults(activeUserId);
    const { mutateAsync: upsertUserSettings } = useUpdateUserSettings(activeUserId);

    const handleToggleCorridors = useCallback(() => {
        upsertUserSettings({ showCorridors: !userSettings.showCorridors });
    }, [upsertUserSettings, userSettings.showCorridors]);

    const handleToggleGoal = useCallback(() => {
        upsertUserSettings({ showGoal: !userSettings.showGoal });
    }, [upsertUserSettings, userSettings.showGoal]);

    const handleToggleComparison = useCallback(() => {
        upsertUserSettings({ showComparison: !userSettings.showComparison });
    }, [upsertUserSettings, userSettings.showComparison]);

    const [comparison, toggleComparison] = useToggle();
    const [visualizations, toggleVisualizations] = useToggle();
    const [showVisualizations, setShowVisualizaitons] = useAtom(showVisualizationsAtom);

    const hasFullAnalysis = useAtomValue(hasFullAnalysisAtom);

    const t = useTranslate();

    return (
        <Box position="relative">
            <Box className={css.backdrop({ isVisible })} onClick={() => setIsVisible(false)} />
            <Box className={css.container}>
                <Only bp="xs" spans={2}>
                    <Box display="flex" position="absolute" top={u(8)} right={8}>
                        <ActionButton icon="menu" variant="primary" buttonSize="tiny" onClick={toggleVisibility} />
                    </Box>
                </Only>
                <Box className={css.actions({ isVisible })}>
                    <div className={css.actionsTitle}>
                        <Heading3>Display</Heading3>
                        <ActionButton icon="close" buttonSize="tiny" variant="muted" onClick={toggleVisibility} />
                    </div>
                    <ToggleButton icon="edit" isActive={isEditingLayout} onChange={onEditLayout}>
                        Edit Layout
                    </ToggleButton>
                    <ToggleButton icon="monitor" isActive={isEditingFloor} onChange={onEditFloor}>
                        Edit Floor View
                    </ToggleButton>
                    {hasFullAnalysis && (
                        <ToggleButtonV2
                            isActive={showVisualizations}
                            onChange={setShowVisualizaitons}
                            onClick={() => toggleVisualizations(true)}
                        >
                            <Text variant="h4" p={u(0.5)}>
                                {t('common.visualizations')}
                            </Text>
                        </ToggleButtonV2>
                    )}
                    <ToggleButtonV2
                        isActive={userSettings.showComparison}
                        onChange={handleToggleComparison}
                        onClick={() => toggleComparison(true)}
                    >
                        <Text variant="h4" p={u(0.5)}>
                            {t('common.comparison')}
                        </Text>
                    </ToggleButtonV2>

                    <ToggleButton
                        isActive={userSettings?.showCorridors}
                        icon="corridor"
                        onChange={handleToggleCorridors}
                    >
                        corridors
                    </ToggleButton>
                    <ToggleButton isActive={userSettings?.showGoal} icon="corridor" onChange={handleToggleGoal}>
                        goal
                    </ToggleButton>

                    <div className={css.notesContainer}>
                        <ToggleButton isActive={showThreads} icon="notes" onChange={() => setShowThreads(!showThreads)}>
                            notes
                        </ToggleButton>
                        <button
                            disabled={!activeSwingId}
                            className={css.addNoteMobile({ disabled: !activeSwingId })}
                            onClick={onCreateNote}
                        >
                            <PlusIcon color={colors.white} />
                        </button>
                    </div>
                </Box>
            </Box>

            {visualizations && <VisualizationsModal onClose={() => toggleVisualizations(false)} />}
            {comparison && <ComparisonSwingModal open onDismiss={toggleComparison} />}
        </Box>
    );
};
